import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, User, FileText, X, ArrowRight } from 'lucide-react';
import { useThemeStore } from '@alvo/store';
import { useNavigate } from 'react-router-dom';

// Determine contact detail route type based on product
function getContactRoute(contact: any): string {
  const product = (contact.product || '').toLowerCase();
  if (['aca', 'aca_health'].includes(product)) return 'aca';
  if (['life', 'fe'].includes(product)) return 'life';
  if (['private', 'private_health'].includes(product)) return 'private';
  if (['ancillary'].includes(product)) return 'ancillary';
  return 'medicare';
}

// Highlight matching text
function Highlight({ text, query }: { text: string; query: string }) {
  if (!query || query.length < 2 || !text) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ fontWeight: 700, textDecoration: 'underline', textDecorationColor: 'rgba(0,113,227,0.4)', textUnderlineOffset: '2px' }}>{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

interface SpotlightSearchProps {
  query?: string;
  onQueryChange?: (q: string) => void;
  isOpen: boolean;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
}

export default function SpotlightSearch({ isOpen, onClose, inputRef, query: externalQuery, onQueryChange }: SpotlightSearchProps) {
  const { colors } = useThemeStore();
  const navigate = useNavigate();
  const [internalQuery, setInternalQuery] = useState('');
  const query = externalQuery !== undefined ? externalQuery : internalQuery;
  const setQuery = onQueryChange || setInternalQuery;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [results, setResults] = useState<{ contacts: any[]; policies: any[] }>({ contacts: [], policies: [] });
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeInputRef = inputRef || useRef<HTMLInputElement>(null);

  // Server-side search (Apple approach: no local state, always fresh from server)
  const searchBackend = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults({ contacts: [], policies: [] });
      return;
    }
    
    setIsSearching(true);
    try {
      const token = localStorage.getItem('alvo-token');
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const resp = await fetch(`/api/v1/search?q=${encodeURIComponent(searchQuery)}&limit=10`, { headers });
      if (resp.ok) {
        const data = await resp.json();
        const backendResults = data.results || {};
        
        // Map backend results to display format
        const contacts = (backendResults.contacts || []).map((c: any) => ({
          type: 'contact' as const,
          id: c.id,
          title: c.name || 'Unknown',
          subtitle: [c.phone, c.email, c.status].filter(Boolean).join(' · '),
          product: c.product,
          meta: c,
        }));

        const policies = (backendResults.policies || []).map((p: any) => ({
          type: 'policy' as const,
          id: p.id,
          title: p.policyNumber || p.clientName || 'Unknown',
          subtitle: [p.clientName, p.carrier, p.planName].filter(Boolean).join(' · '),
          contactId: p.contactId,
          meta: p,
        }));

        setResults({ contacts, policies });
      }
    } catch (err) {
      console.error('Search failed:', err);
      setResults({ contacts: [], policies: [] });
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounce search (300ms - Apple standard)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length >= 2) {
      debounceRef.current = setTimeout(() => searchBackend(query), 300);
    } else {
      setResults({ contacts: [], policies: [] });
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, searchBackend]);

  // Flatten results for keyboard navigation
  const allResults = [...results.contacts, ...results.policies];

  // Handle selection
  const handleSelect = useCallback((item: any) => {
    if (item.type === 'contact') {
      const route = getContactRoute(item);
      navigate(`/contacts/${route}/${item.id}`);
    } else if (item.type === 'policy') {
      navigate(`/policies/${item.id}`);
    }
    onClose();
    setQuery('');
  }, [navigate, onClose, setQuery]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % allResults.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + allResults.length) % allResults.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (allResults[selectedIndex]) handleSelect(allResults[selectedIndex]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        setQuery('');
      }
    };
    
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, selectedIndex, allResults, handleSelect, onClose, setQuery]);

  // Reset selected index when query changes
  useEffect(() => { setSelectedIndex(0); }, [query]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node) && 
          activeInputRef.current && !activeInputRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Calculate position from the input ref
  const [dropdownPos, setDropdownPos] = React.useState({ top: 0, left: 0, width: 0 });
  React.useEffect(() => {
    if (isOpen && query.length >= 2 && activeInputRef.current) {
      const rect = activeInputRef.current.closest('div')?.getBoundingClientRect();
      if (rect) {
        setDropdownPos({ top: rect.bottom + 8, left: rect.left, width: rect.width });
      }
    }
  }, [isOpen, query]);

  const showDropdown = isOpen && query.length >= 2;

  if (!showDropdown) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width || 400,
        background: colors.cardBg,
        border: `1px solid ${colors.border}`,
        borderRadius: '14px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.05)',
        zIndex: 9999,
        overflow: 'hidden',
        maxHeight: '480px',
        overflowY: 'auto',
      }}
    >
      {isSearching && allResults.length === 0 ? (
        <div style={{ padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: '15px', color: colors.textSecondary }}>
            Searching...
          </div>
        </div>
      ) : allResults.length === 0 ? (
        <div style={{ padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: '15px', color: colors.textSecondary, marginBottom: '4px' }}>
            No results for "{query}"
          </div>
          <div style={{ fontSize: '13px', color: colors.textTertiary }}>
            Try a name, phone number, or policy ID
          </div>
        </div>
      ) : (
        <>
          {/* Contacts Section */}
          {results.contacts.length > 0 && (
            <>
              <div style={{ padding: '10px 16px 4px', fontSize: '11px', fontWeight: 600, color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Contacts
              </div>
              {results.contacts.map((item: any, idx: number) => {
                const globalIdx = idx;
                const isSelected = selectedIndex === globalIdx;
                return (
                  <div
                    key={`c-${item.id}`}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setSelectedIndex(globalIdx)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      cursor: 'pointer',
                      background: isSelected ? `${colors.primary}15` : 'transparent',
                      transition: 'background 0.1s ease',
                    }}
                  >
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '10px',
                      background: `${colors.primary}20`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <User size={16} color={colors.primary} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Highlight text={item.title} query={query} />
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Highlight text={item.subtitle} query={query} />
                      </div>
                    </div>
                    {isSelected && <ArrowRight size={14} color={colors.textTertiary} />}
                  </div>
                );
              })}
            </>
          )}

          {/* Policies Section */}
          {results.policies.length > 0 && (
            <>
              <div style={{
                padding: '10px 16px 4px', fontSize: '11px', fontWeight: 600,
                color: colors.textTertiary, textTransform: 'uppercase', letterSpacing: '0.5px',
                borderTop: results.contacts.length > 0 ? `1px solid ${colors.border}` : 'none',
              }}>
                Policies
              </div>
              {results.policies.map((item: any, idx: number) => {
                const globalIdx = results.contacts.length + idx;
                const isSelected = selectedIndex === globalIdx;
                return (
                  <div
                    key={`p-${item.id}`}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setSelectedIndex(globalIdx)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      cursor: 'pointer',
                      background: isSelected ? `${colors.primary}15` : 'transparent',
                      transition: 'background 0.1s ease',
                    }}
                  >
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '10px',
                      background: `${colors.success}20`, display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <FileText size={16} color={colors.success} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: 500, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Highlight text={item.title} query={query} />
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <Highlight text={item.subtitle} query={query} />
                      </div>
                    </div>
                    {isSelected && <ArrowRight size={14} color={colors.textTertiary} />}
                  </div>
                );
              })}
            </>
          )}
        </>
      )}
    </div>
  );
}
